/**
 * Release Timeline Types
 * 
 * Type definitions for release timeline functionality including categorization,
 * swimlane configuration, and enhanced release data structures.
 */

import { ProjectVersion } from '../services/apiService'

// ============================================================================
// RELEASE CATEGORIZATION
// ============================================================================

/**
 * Release categories for swimlane assignment
 */
export enum ReleaseCategory {
  SOFTWARE = 'Software',
  CLOUD = 'Cloud', 
  AZURE = 'Azure',
  OSS = 'OSS'
}

/**
 * Keywords for categorizing releases into swimlanes
 */
export const RELEASE_KEYWORDS = {
  [ReleaseCategory.SOFTWARE]: ['SW', 'Software', 'Maint', 'maint', 'Cross', 'k8s', 'patch'],
  [ReleaseCategory.CLOUD]: ['Cloud', 'cloud', 'RCP', 'SM', 'CAPI', 'Cross'],
  [ReleaseCategory.AZURE]: ['Cross', 'Azure'],
  [ReleaseCategory.OSS]: ['OSS', 'OS']
} as const

/**
 * Special keywords that trigger cross-category duplication
 * When a release contains these keywords, it will appear in multiple swimlanes
 */
export const CROSS_CATEGORY_KEYWORDS = {
  'Cross': [ReleaseCategory.SOFTWARE, ReleaseCategory.CLOUD, ReleaseCategory.AZURE]
} as const

/**
 * Category precedence for handling overlapping keywords
 * Higher index = higher precedence
 */
export const CATEGORY_PRECEDENCE = [
  ReleaseCategory.OSS,      // Lowest precedence
  ReleaseCategory.AZURE,
  ReleaseCategory.CLOUD,
  ReleaseCategory.SOFTWARE  // Highest precedence
] as const

// ============================================================================
// ENHANCED RELEASE INTERFACES
// ============================================================================

/**
 * Extended ProjectVersion with categorization and swimlane assignment
 */
export interface CategorizedRelease extends ProjectVersion {
  /** Assigned category based on keyword matching */
  category?: ReleaseCategory

  /** Swimlane index (0-3 for Software, Cloud, Azure, OSS) */
  swimlaneIndex?: number

  /** Matched keywords that led to categorization */
  matchedKeywords?: string[]

  /** Whether this release matches any category */
  isCategorized: boolean

  /** Computed display properties */
  displayName: string
  displayDescription?: string

  /** For cross-category releases: all categories this release appears in */
  allCategories?: ReleaseCategory[]

  /** For cross-category releases: indicates this is a duplicated release */
  isCrossCategoryDuplicate?: boolean

  /** For cross-category releases: reference to the original release ID */
  originalReleaseId?: string
}

/**
 * Swimlane configuration for timeline display
 */
export interface SwimLaneConfig {
  /** Swimlane category */
  category: ReleaseCategory
  
  /** Display label for the swimlane */
  label: string
  
  /** Swimlane index (0-3) */
  index: number
  
  /** Primary color for releases in this swimlane */
  color: string
  
  /** Background color for the swimlane */
  backgroundColor: string
  
  /** Text color for labels */
  textColor: string
  
  /** Keywords used for categorization */
  keywords: readonly string[]
  
  /** Whether this swimlane is currently visible */
  visible: boolean
}

/**
 * Processed release timeline data with positioning and styling
 */
export interface ReleaseTimelineData {
  /** Original categorized release data */
  release: CategorizedRelease
  
  /** Timeline positioning */
  position: {
    /** Horizontal position (left offset in pixels) */
    left: number
    /** Width in pixels */
    width: number
    /** Vertical position (swimlane index) */
    swimlane: number
    /** Whether the release is visible in current viewport */
    isVisible: boolean
  }
  
  /** Visual styling */
  style: {
    /** Primary color */
    color: string
    /** Background gradient */
    gradient: string
    /** Border color */
    borderColor: string
    /** Text color */
    textColor: string
  }
  
  /** Duration information */
  duration: {
    /** Duration in days (for upcoming releases, days until release) */
    days: number
    /** Human-readable duration text */
    text: string
  }
  
  /** Interaction state */
  state: {
    /** Whether the release is currently hovered */
    isHovered: boolean
    /** Whether the release is currently selected */
    isSelected: boolean
  }
}

/**
 * Release timeline configuration
 */
export interface ReleaseTimelineConfig {
  /** Swimlane configurations */
  swimlanes: SwimLaneConfig[]
  
  /** Date filtering */
  dateFilter: {
    /** Minimum date to display (September 2025) */
    minDate: Date
    /** Maximum date to display (optional) */
    maxDate?: Date
  }
  
  /** Visual configuration */
  visual: {
    /** Container height */
    height: number
    /** Swimlane height */
    swimlaneHeight: number
    /** Spacing between swimlanes */
    swimlaneSpacing: number
    /** Show today marker */
    showToday: boolean
    /** Show grid lines */
    showGrid: boolean
    /** Show swimlane labels */
    showLabels: boolean
  }
  
  /** Interaction configuration */
  interaction: {
    /** Enable click interactions */
    enableClick: boolean
    /** Enable hover interactions */
    enableHover: boolean
    /** Enable keyboard navigation */
    enableKeyboard: boolean
  }
}

/**
 * Release categorization result
 */
export interface CategorizationResult {
  /** Assigned category (if any) */
  category?: ReleaseCategory
  
  /** Matched keywords */
  matchedKeywords: string[]
  
  /** Whether categorization was successful */
  success: boolean
  
  /** Confidence score (0-1) */
  confidence: number
  
  /** Reason for categorization or failure */
  reason: string
}

/**
 * Release timeline filter options
 */
export interface ReleaseTimelineFilters {
  /** Filter by categories */
  categories?: ReleaseCategory[]
  
  /** Filter by date range */
  dateRange?: {
    start: Date
    end: Date
  }
  
  /** Filter by release status */
  status?: ('released' | 'unreleased')[]
  
  /** Text search in release names/descriptions */
  searchText?: string
  
  /** Show only categorized releases */
  showOnlyCategorized?: boolean
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Release timeline state management
 */
export interface ReleaseTimelineState {
  /** Currently loaded releases */
  releases: CategorizedRelease[]
  
  /** Processed timeline data */
  timelineData: ReleaseTimelineData[]
  
  /** Current filters */
  filters: ReleaseTimelineFilters
  
  /** Loading state */
  isLoading: boolean
  
  /** Error state */
  error: Error | null
  
  /** Selected release */
  selectedRelease: CategorizedRelease | null
  
  /** Hovered release */
  hoveredRelease: CategorizedRelease | null
}

/**
 * Release timeline actions
 */
export interface ReleaseTimelineActions {
  /** Load releases from API */
  loadReleases: () => Promise<void>
  
  /** Apply filters */
  applyFilters: (filters: Partial<ReleaseTimelineFilters>) => void
  
  /** Select a release */
  selectRelease: (release: CategorizedRelease | null) => void
  
  /** Hover over a release */
  hoverRelease: (release: CategorizedRelease | null) => void
  
  /** Refresh data */
  refresh: () => Promise<void>
}
